import { Form, Formik } from "formik";
import { useEffect, useReducer, useState } from "react";
import { FiArrowLeft, FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Loader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  portalForgot,
  portalLogin,
  portalResend2fa,
  portalSelectClient,
  portalSetPassword,
  portalSignup,
  portalVerify2fa,
  resolvePortal,
} from "@/features/portal/api";
import { usePortalSessionStore } from "@/features/portal/store/portal-session-store";
import { portalHref } from "@/features/portal/portal-base";
import type { AuthStep, PortalClientSummary } from "@/features/portal/types";
import { ApiError } from "@/lib/api";
import {
  isStrongPortalPassword,
  PasswordStrengthChecklist,
} from "./password-strength";

// --- phase state machine -----------------------------------------------------

type Phase =
  | "resolving"
  | "inaccessible"
  | "login"
  | "signup"
  | "forgot"
  | "twofactor"
  | "setpass"
  | "select"
  | "locked";

interface AuthState {
  phase: Phase;
  pendingToken: string;
  clients: PortalClientSummary[];
  error: string;
  lockedMessage: string;
}

type Action =
  | { type: "inaccessible" }
  | { type: "goto"; phase: Extract<Phase, "login" | "signup" | "forgot"> }
  | { type: "twofactor"; token: string }
  | { type: "setpass"; token: string }
  | { type: "select"; token: string; clients: PortalClientSummary[] }
  | { type: "locked"; message: string }
  | { type: "error"; message: string };

const initialState: AuthState = {
  phase: "resolving",
  pendingToken: "",
  clients: [],
  error: "",
  lockedMessage: "",
};

function reducer(state: AuthState, action: Action): AuthState {
  switch (action.type) {
    case "inaccessible":
      return { ...state, phase: "inaccessible" };
    case "goto":
      return { ...state, phase: action.phase, error: "" };
    case "twofactor":
      return { ...state, phase: "twofactor", pendingToken: action.token, error: "" };
    case "setpass":
      return { ...state, phase: "setpass", pendingToken: action.token, error: "" };
    case "select":
      return {
        ...state,
        phase: "select",
        pendingToken: action.token,
        clients: action.clients,
        error: "",
      };
    case "locked":
      return { ...state, phase: "locked", lockedMessage: action.message, error: "" };
    case "error":
      return { ...state, error: action.message };
    default:
      return state;
  }
}

const LOCKED_MESSAGE =
  "Your account is locked. Try again later or contact your firm.";

// --- validation schemas ------------------------------------------------------

const emailSchema = Yup.string()
  .email("Enter a valid email")
  .required("Email is required");

const loginSchema = Yup.object({
  email: emailSchema,
  password: Yup.string().required("Password is required"),
});

const identitySchema = Yup.object({
  email: emailSchema,
  tin: Yup.string()
    .matches(/^\d{4}$/, "Enter the last 4 digits")
    .required("Last 4 of your SSN or EIN is required"),
});

const setpassSchema = Yup.object({
  password: Yup.string()
    .required("Enter a password")
    .test("strength", "Password does not meet the requirements", (v) =>
      isStrongPortalPassword(v ?? ""),
    ),
});

// --- component ---------------------------------------------------------------

export function PortalAuthCard() {
  const { portalName } = useParams<{ portalName: string }>();
  const navigate = useNavigate();
  const setBranding = usePortalSessionStore((s) => s.setBranding);
  const setPortalName = usePortalSessionStore((s) => s.setPortalName);
  const setSession = usePortalSessionStore((s) => s.setSession);
  const branding = usePortalSessionStore((s) => s.branding);
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!portalName) {
      dispatch({ type: "inaccessible" });
      return;
    }
    // Record the handle the taxpayer is using as soon as we land on their portal
    // URL — before the async resolve — so post-auth redirects always point at
    // this firm's branded sign-in and never a previously-cached firm's handle.
    // (Authoritative for redirects, unlike the firm-config `branding.portalName`.)
    setPortalName(portalName);
    let active = true;
    resolvePortal(portalName)
      .then((res) => {
        if (!active) return;
        if (!res.enabled) {
          dispatch({ type: "inaccessible" });
          return;
        }
        if (res.branding) setBranding(res.branding);
        dispatch({ type: "goto", phase: "login" });
      })
      .catch(() => {
        if (active) dispatch({ type: "inaccessible" });
      });
    return () => {
      active = false;
    };
  }, [portalName, setBranding, setPortalName]);

  /** Advance from a verify/setpass/select response: a bound session lands home,
   * otherwise route to the next phase. */
  function handleStep(step: AuthStep) {
    // A bound session has a token + client; branding falls back to what the
    // resolve step already put in the store (the backend also echoes it).
    if (step.token && step.client) {
      const boundBranding = step.branding ?? branding;
      if (boundBranding) {
        setSession(step.token, step.client, boundBranding);
        navigate(portalHref("/home"));
        return;
      }
    }
    if (step.stage === "setpass" && step.token) {
      dispatch({ type: "setpass", token: step.token });
      return;
    }
    if (step.stage === "select" && step.token) {
      dispatch({ type: "select", token: step.token, clients: step.clients ?? [] });
      return;
    }
    dispatch({ type: "error", message: "Something went wrong. Please try again." });
  }

  const firmName = branding?.firmName ?? "Client Portal";

  function renderHeader(title: string, description?: string) {
    return (
      <CardHeader className="text-center">
        {branding?.showName && branding.firmName && (
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {branding.firmName}
          </p>
        )}
        <CardTitle className="text-xl">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
    );
  }

  let body: React.ReactNode;

  if (state.phase === "resolving") {
    body = (
      <CardContent className="flex justify-center py-10">
        <Loader />
      </CardContent>
    );
  } else if (state.phase === "inaccessible") {
    body = (
      <>
        {renderHeader("Portal unavailable")}
        <CardContent className="text-muted-foreground text-center text-sm">
          This portal is not available — contact your firm.
        </CardContent>
      </>
    );
  } else if (state.phase === "locked") {
    body = (
      <>
        {renderHeader("Account locked")}
        <CardContent className="grid gap-6 text-center">
          <p className="text-muted-foreground text-sm">
            {state.lockedMessage || LOCKED_MESSAGE}
          </p>
          <Button
            variant="outline"
            className="h-11"
            onClick={() => dispatch({ type: "goto", phase: "login" })}
          >
            Back to sign in
          </Button>
        </CardContent>
      </>
    );
  } else if (state.phase === "login") {
    body = (
      <>
        {renderHeader(`Sign in to ${firmName}`)}
        <CardContent>
          <LoginPhase
            portalName={portalName!}
            error={state.error}
            dispatch={dispatch}
          />
        </CardContent>
      </>
    );
  } else if (state.phase === "signup") {
    body = (
      <>
        {renderHeader("Create your portal account")}
        <CardContent>
          <IdentityPhase
            portalName={portalName!}
            action="signup"
            submitLabel="Send code"
            error={state.error}
            dispatch={dispatch}
          />
        </CardContent>
      </>
    );
  } else if (state.phase === "forgot") {
    body = (
      <>
        {renderHeader("Reset your password")}
        <CardContent>
          <IdentityPhase
            portalName={portalName!}
            action="forgot"
            submitLabel="Send code"
            error={state.error}
            dispatch={dispatch}
          />
        </CardContent>
      </>
    );
  } else if (state.phase === "twofactor") {
    body = (
      <>
        {renderHeader("Enter your code", "We emailed you a 6-digit security code.")}
        <CardContent>
          <TwoFactorPhase
            token={state.pendingToken}
            error={state.error}
            dispatch={dispatch}
            onStep={handleStep}
          />
        </CardContent>
      </>
    );
  } else if (state.phase === "setpass") {
    body = (
      <>
        {renderHeader("Create a password")}
        <CardContent>
          <SetPasswordPhase
            token={state.pendingToken}
            error={state.error}
            dispatch={dispatch}
            onStep={handleStep}
          />
        </CardContent>
      </>
    );
  } else {
    // select
    body = (
      <>
        {renderHeader("Choose a client", "Select which client you'd like to access.")}
        <CardContent>
          <SelectClientPhase
            token={state.pendingToken}
            clients={state.clients}
            error={state.error}
            dispatch={dispatch}
            onStep={handleStep}
          />
        </CardContent>
      </>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md">
      <Card>{body}</Card>
    </div>
  );
}

// --- phase sub-components -----------------------------------------------------

function ErrorText({ message }: { message: string }) {
  if (!message) return null;
  return <p className="text-destructive text-center text-sm">{message}</p>;
}

function BackToLogin({ dispatch }: { dispatch: React.Dispatch<Action> }) {
  return (
    <button
      type="button"
      onClick={() => dispatch({ type: "goto", phase: "login" })}
      className="text-muted-foreground hover:text-foreground flex items-center justify-center gap-2 text-sm transition-colors"
    >
      <FiArrowLeft className="size-4" />
      Back to sign in
    </button>
  );
}

function LoginPhase({
  portalName,
  error,
  dispatch,
}: {
  portalName: string;
  error: string;
  dispatch: React.Dispatch<Action>;
}) {
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(values: { email: string; password: string }) {
    try {
      const step = await portalLogin(portalName, values);
      if (step.stage === "twofactor" && step.token) {
        dispatch({ type: "twofactor", token: step.token });
        return;
      }
      dispatch({ type: "error", message: "Something went wrong. Please try again." });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        dispatch({ type: "locked", message: err.message || LOCKED_MESSAGE });
        return;
      }
      dispatch({
        type: "error",
        message: "We couldn't sign you in. Check your details and try again.",
      });
    }
  }

  return (
    <Formik
      initialValues={{ email: "", password: "" }}
      validationSchema={loginSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="grid gap-6">
          <FormField
            name="email"
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="h-11"
          />
          <FormField
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder="••••••••"
            className="h-11"
            labelAdornment={
              <button
                type="button"
                onClick={() => dispatch({ type: "goto", phase: "forgot" })}
                className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
              >
                Forgot password?
              </button>
            }
            endAdornment={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <FiEye className="size-4" />
                ) : (
                  <FiEyeOff className="size-4" />
                )}
              </button>
            }
          />
          <ErrorText message={error} />
          <Button type="submit" disabled={isSubmitting} className="h-11">
            {isSubmitting ? "Signing in..." : "Sign in"}
          </Button>
          <p className="text-muted-foreground text-center text-sm">
            Don&apos;t have an account?{" "}
            <button
              type="button"
              onClick={() => dispatch({ type: "goto", phase: "signup" })}
              className="text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Sign Up
            </button>
          </p>
        </Form>
      )}
    </Formik>
  );
}

function IdentityPhase({
  portalName,
  action,
  submitLabel,
  error,
  dispatch,
}: {
  portalName: string;
  action: "signup" | "forgot";
  submitLabel: string;
  error: string;
  dispatch: React.Dispatch<Action>;
}) {
  async function handleSubmit(values: { email: string; tin: string }) {
    try {
      const step =
        action === "signup"
          ? await portalSignup(portalName, values)
          : await portalForgot(portalName, values);
      if (step.stage === "twofactor" && step.token) {
        dispatch({ type: "twofactor", token: step.token });
        return;
      }
      dispatch({ type: "error", message: "Something went wrong. Please try again." });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        dispatch({ type: "locked", message: err.message || LOCKED_MESSAGE });
        return;
      }
      dispatch({
        type: "error",
        message: "We couldn't verify those details. Please check and try again.",
      });
    }
  }

  return (
    <Formik
      initialValues={{ email: "", tin: "" }}
      validationSchema={identitySchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="grid gap-6">
          <FormField
            name="email"
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="h-11"
          />
          <FormField
            name="tin"
            label="Last 4 of SSN or EIN"
            inputMode="numeric"
            maxLength={4}
            placeholder="1234"
            className="h-11"
          />
          <ErrorText message={error} />
          <Button type="submit" disabled={isSubmitting} className="h-11">
            {isSubmitting ? "Sending..." : submitLabel}
          </Button>
          <BackToLogin dispatch={dispatch} />
        </Form>
      )}
    </Formik>
  );
}

function TwoFactorPhase({
  token,
  error,
  dispatch,
  onStep,
}: {
  token: string;
  error: string;
  dispatch: React.Dispatch<Action>;
  onStep: (step: AuthStep) => void;
}) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(value: string) {
    setSubmitting(true);
    try {
      const step = await portalVerify2fa({ token, code: value });
      onStep(step);
    } catch (err) {
      if (err instanceof ApiError) {
        const locked =
          err.status === 403 ||
          Boolean(
            err.data &&
              typeof err.data === "object" &&
              (err.data as Record<string, unknown>).locked,
          );
        if (locked) {
          dispatch({ type: "locked", message: err.message || LOCKED_MESSAGE });
          return;
        }
      }
      setCode("");
      dispatch({
        type: "error",
        message: "That code is incorrect or has expired. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function resend() {
    try {
      await portalResend2fa(token);
      toast.success("Code sent");
    } catch {
      toast.error("Couldn't resend the code. Please try again.");
    }
  }

  return (
    <div className="grid gap-6">
      <div className="flex flex-col items-center gap-3">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={setCode}
          onComplete={submit}
          disabled={submitting}
          aria-label="Security code"
        >
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} />
            ))}
          </InputOTPGroup>
        </InputOTP>
        <button
          type="button"
          onClick={resend}
          className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
        >
          Resend code
        </button>
      </div>
      <ErrorText message={error} />
      <Button
        type="button"
        disabled={submitting || code.length !== 6}
        className="h-11"
        onClick={() => submit(code)}
      >
        {submitting ? "Verifying..." : "Verify"}
      </Button>
      <BackToLogin dispatch={dispatch} />
    </div>
  );
}

function SetPasswordPhase({
  token,
  error,
  dispatch,
  onStep,
}: {
  token: string;
  error: string;
  dispatch: React.Dispatch<Action>;
  onStep: (step: AuthStep) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(values: { password: string }) {
    try {
      const step = await portalSetPassword({ token, password: values.password });
      onStep(step);
    } catch {
      dispatch({
        type: "error",
        message: "We couldn't set your password. Please try again.",
      });
    }
  }

  return (
    <Formik
      initialValues={{ password: "" }}
      validationSchema={setpassSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values }) => (
        <Form className="grid gap-6">
          <div className="grid gap-3">
            <FormField
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="••••••••"
              className="h-11"
              endAdornment={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <FiEye className="size-4" />
                  ) : (
                    <FiEyeOff className="size-4" />
                  )}
                </button>
              }
            />
            <PasswordStrengthChecklist value={values.password} />
          </div>
          <ErrorText message={error} />
          <Button type="submit" disabled={isSubmitting} className="h-11">
            {isSubmitting ? "Saving..." : "Set password"}
          </Button>
        </Form>
      )}
    </Formik>
  );
}

function SelectClientPhase({
  token,
  clients,
  error,
  dispatch,
  onStep,
}: {
  token: string;
  clients: PortalClientSummary[];
  error: string;
  dispatch: React.Dispatch<Action>;
  onStep: (step: AuthStep) => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);

  async function pick(accessId: string) {
    setBusyId(accessId);
    try {
      const step = await portalSelectClient({ token, access_id: accessId });
      onStep(step);
    } catch {
      dispatch({
        type: "error",
        message: "We couldn't open that client. Please try again.",
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-2">
        {clients.map((client) => (
          <button
            key={client.accessId}
            type="button"
            disabled={busyId !== null}
            onClick={() => pick(client.accessId)}
            className="border-border hover:bg-accent hover:text-accent-foreground flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors disabled:opacity-60"
          >
            <span className="font-medium">{client.name}</span>
            <span className="text-muted-foreground text-xs">
              {client.party === "S" ? "Spouse" : "Taxpayer"}
            </span>
          </button>
        ))}
      </div>
      <ErrorText message={error} />
    </div>
  );
}
